import WmsLayer from './WmsLayer';
import { pzgikAttribution } from './attribution';
import { removeSourceAndLayers, wmsSourceTiles } from '../utils/maplibre-helpers';

const OVERVIEW_LAYER_ID = 'ortho-overview';
const OVERVIEW_SOURCE_ID = 'src-ortho-overview';
const OVERVIEW_URL = '/img/map/ortho-overview.webp';
const OVERVIEW_MIN_ZOOM = 5;
const OVERVIEW_MAX_ZOOM = 10.5;
const STANDARD_LAYER_MIN_ZOOM = 10;
const HIGH_RESOLUTION_LAYER_ID = 'ortho-hires';
const HIGH_RESOLUTION_SOURCE_ID = 'src-ortho-hires';
const HIGH_RESOLUTION_MIN_ZOOM = 18;
const HIGH_RESOLUTION_SOURCE_MAX_ZOOM = 20;
const OVERVIEW_EXTENT = {
    west: 13.75,
    south: 48.75,
    east: 24.75,
    north: 55.1
};
const CONTEXT_LAYER_IDS = [
    'ortho-context-voivodeship-halo',
    'ortho-context-voivodeship',
    'ortho-context-county-halo',
    'ortho-context-county',
    'ortho-context-municipality-halo',
    'ortho-context-municipality',
    'ortho-context-street-major',
    'ortho-context-street-minor',
    'ortho-context-street-path',
    'ortho-context-regional-capital'
];

function administrativeBoundaryFilter(level) {
    return [
        'all',
        ['==', ['get', 'admin_level'], level],
        ['!=', ['get', 'maritime'], 1],
        ['!=', ['get', 'disputed'], 1],
        ['!', ['has', 'claimed_by']]
    ];
}

function streetNameField() {
    return ['coalesce', ['get', 'name:pl'], ['get', 'name']];
}

function resolveOpenMapTilesSource(map) {
    const layers = (map.getStyle() && map.getStyle().layers) || [];
    const reference = layers.find((layer) => (
        layer && typeof layer.source === 'string'
        && ['boundary', 'place', 'transportation_name'].indexOf(layer['source-layer']) !== -1
    ));
    return reference ? reference.source : null;
}

function addContextLayer(map, layer, beforeId) {
    if (!map.getLayer(layer.id)) map.addLayer(layer, beforeId);
}

export default class OrthoLayer extends WmsLayer {
    static ID = 'ortho';
    static DESCRIPTION = 'Ortofotomapa';
    static MIN_ZOOM = 5;

    getWmsConfig() {
        return {
            url: 'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/StandardResolution',
            params: {
                LAYERS: 'Raster',
                FORMAT: 'image/png',
                TRANSPARENT: 'true',
                VERSION: '1.3.0',
                WIDTH: 512,
                HEIGHT: 512
            },
            opacity: 1,
            tileSize: 512,
            layerMinZoom: STANDARD_LAYER_MIN_ZOOM,
            sourceMaxZoom: 18,
            attribution: pzgikAttribution()
        };
    }

    getHighResolutionWmsConfig() {
        return {
            url: 'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/HighResolution',
            params: {
                LAYERS: 'Raster',
                FORMAT: 'image/png',
                TRANSPARENT: 'true',
                VERSION: '1.3.0',
                WIDTH: 512,
                HEIGHT: 512
            },
            opacity: 1,
            tileSize: 512,
            sourceMaxZoom: HIGH_RESOLUTION_SOURCE_MAX_ZOOM
        };
    }

    addOverviewLayer(map) {
        if (map.getSource(OVERVIEW_SOURCE_ID)) return;

        map.addSource(OVERVIEW_SOURCE_ID, {
            type: 'image',
            url: OVERVIEW_URL,
            coordinates: [
                [OVERVIEW_EXTENT.west, OVERVIEW_EXTENT.north],
                [OVERVIEW_EXTENT.east, OVERVIEW_EXTENT.north],
                [OVERVIEW_EXTENT.east, OVERVIEW_EXTENT.south],
                [OVERVIEW_EXTENT.west, OVERVIEW_EXTENT.south]
            ]
        });
        map.addLayer({
            id: OVERVIEW_LAYER_ID,
            type: 'raster',
            source: OVERVIEW_SOURCE_ID,
            minzoom: OVERVIEW_MIN_ZOOM,
            maxzoom: OVERVIEW_MAX_ZOOM,
            paint: {
                'raster-opacity': 1,
                'raster-fade-duration': 0
            }
        }, this.id);
    }

    addContextLayers(map, beforeId) {
        const source = resolveOpenMapTilesSource(map);
        if (!source) return;

        const boundaryLayers = [
            { key: 'voivodeship', level: 4, minzoom: 5, color: '#8f3d16', haloWidth: 4, width: 2 },
            { key: 'county', level: 6, minzoom: 9, color: '#7a4d2b', haloWidth: 3, width: 1.35 },
            { key: 'municipality', level: 7, minzoom: 11, color: '#5f5147', haloWidth: 2.4, width: 1, dasharray: [3, 2] }
        ];
        boundaryLayers.forEach((definition) => {
            const common = {
                type: 'line',
                source: source,
                'source-layer': 'boundary',
                minzoom: definition.minzoom,
                filter: administrativeBoundaryFilter(definition.level)
            };
            addContextLayer(map, {
                ...common,
                id: 'ortho-context-' + definition.key + '-halo',
                paint: {
                    'line-color': 'rgba(255,255,255,.9)',
                    'line-width': definition.haloWidth,
                    'line-opacity': 0.9
                }
            }, beforeId);
            const paint = {
                'line-color': definition.color,
                'line-width': definition.width,
                'line-opacity': 0.95
            };
            if (definition.dasharray) paint['line-dasharray'] = definition.dasharray;
            addContextLayer(map, {
                ...common,
                id: 'ortho-context-' + definition.key,
                paint: paint
            }, beforeId);
        });

        const streetLayers = [
            {
                key: 'major', minzoom: 13,
                classes: ['motorway', 'trunk', 'primary', 'secondary', 'tertiary'],
                font: 'Noto Sans Regular', size: 13
            },
            {
                key: 'minor', minzoom: 15,
                classes: ['minor', 'service', 'track'],
                font: 'Noto Sans Regular', size: 12
            },
            {
                key: 'path', minzoom: 16,
                classes: ['path'],
                font: 'Noto Sans Regular', size: 11
            }
        ];
        streetLayers.forEach((definition) => addContextLayer(map, {
            id: 'ortho-context-street-' + definition.key,
            type: 'symbol',
            source: source,
            'source-layer': 'transportation_name',
            minzoom: definition.minzoom,
            filter: ['match', ['get', 'class'], definition.classes, true, false],
            layout: {
                'symbol-placement': 'line',
                'text-field': streetNameField(),
                'text-font': [definition.font],
                'text-size': definition.size,
                'text-rotation-alignment': 'map',
                'text-letter-spacing': 0.02
            },
            paint: {
                'text-color': '#302923',
                'text-halo-color': 'rgba(255,255,255,.96)',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.5
            }
        }, beforeId));

        addContextLayer(map, {
            id: 'ortho-context-regional-capital',
            type: 'symbol',
            source: source,
            'source-layer': 'place',
            minzoom: 5,
            filter: [
                'all',
                ['==', ['get', 'class'], 'city'],
                ['match', ['get', 'capital'], [2, 4], true, false]
            ],
            layout: {
                'text-field': streetNameField(),
                'text-font': ['Noto Sans Bold'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 5, 12, 9, 15],
                'text-anchor': 'bottom',
                'text-offset': [0, -0.4],
                'text-max-width': 9
            },
            paint: {
                'text-color': '#241d18',
                'text-halo-color': 'rgba(255,255,255,.98)',
                'text-halo-width': 2,
                'text-halo-blur': 0.5
            }
        }, beforeId);
    }

    load() {
        super.load();

        const map = this.getMap();
        if (!map || !map.getLayer(this.id)) return;

        this.addOverviewLayer(map);

        if (!map.getSource(HIGH_RESOLUTION_SOURCE_ID)) {
            const highResolution = this.getHighResolutionWmsConfig();
            const source = {
                type: 'raster',
                tiles: [wmsSourceTiles(highResolution.url, highResolution.params)],
                tileSize: highResolution.tileSize || 256
            };
            if (highResolution.sourceMaxZoom != null) source.maxzoom = highResolution.sourceMaxZoom;
            map.addSource(HIGH_RESOLUTION_SOURCE_ID, source);
            map.addLayer({
                id: HIGH_RESOLUTION_LAYER_ID,
                type: 'raster',
                source: HIGH_RESOLUTION_SOURCE_ID,
                minzoom: HIGH_RESOLUTION_MIN_ZOOM,
                maxzoom: this.maxZoom,
                paint: {
                    'raster-opacity': highResolution.opacity != null ? highResolution.opacity : 1,
                    'raster-fade-duration': 0
                }
            });
        }

        const style = map.getStyle();
        const firstDataLayer = style && style.layers && style.layers.find((layer) => {
            if ([OVERVIEW_LAYER_ID, this.id, HIGH_RESOLUTION_LAYER_ID].indexOf(layer.id) !== -1) return false;
            if ([OVERVIEW_SOURCE_ID, this.getSourceId(), HIGH_RESOLUTION_SOURCE_ID].indexOf(layer.source) !== -1) return false;

            return layer.id.indexOf('lyr-') === 0
                || (typeof layer.source === 'string' && layer.source.indexOf('src-') === 0);
        });

        if (firstDataLayer) {
            map.moveLayer(OVERVIEW_LAYER_ID, firstDataLayer.id);
            map.moveLayer(this.id, firstDataLayer.id);
            map.moveLayer(HIGH_RESOLUTION_LAYER_ID, firstDataLayer.id);
        }
        this.addContextLayers(map, firstDataLayer ? firstDataLayer.id : undefined);
    }

    clear() {
        const map = this.getMap();
        if (map) {
            removeSourceAndLayers(map, null, CONTEXT_LAYER_IDS);
            removeSourceAndLayers(map, HIGH_RESOLUTION_SOURCE_ID, [HIGH_RESOLUTION_LAYER_ID]);
            removeSourceAndLayers(map, OVERVIEW_SOURCE_ID, [OVERVIEW_LAYER_ID]);
        }
        super.clear();
    }
}
